import axios, { type AxiosInstance } from 'axios';
import { parseIdentity, type AuthIdentity } from '@/types/identity';

export interface Credentials { username: string; password: string }
export interface SessionResult { accessToken: string; expiresIn: number; user: AuthIdentity }
export interface AuthEndpoints {
  login(credentials: Credentials): Promise<SessionResult>;
  refresh(): Promise<SessionResult>;
  logout(): Promise<void>;
  changePassword(token: string, current: string, next: string): Promise<void>;
  logoutAll(token: string, current: string): Promise<void>;
  revokeSession(token: string, id: string): Promise<void>;
}

export class AuthRequestError extends Error {
  constructor(public readonly statusCode: number) {
    super(statusCode === 401 ? 'Invalid credentials or session' :
      statusCode === 429 ? 'Too many attempts; retry later' : 'Authentication is unavailable');
    this.name = 'AuthRequestError';
  }
}

export function createAuthEndpoints(client: AxiosInstance): AuthEndpoints {
  async function request(path: string, body?: object, token?: string, method: 'post' | 'delete' = 'post') {
    try {
      const response = await client.request({ url: path, method, data: body,
        withCredentials: true,
        headers: { 'Content-Type': 'application/json', 'X-ERP-CSRF': '1', Authorization: token ? `Bearer ${token}` : undefined },
      });
      return response.data;
    } catch (error) {
      // Do not retain an Axios config containing the submitted password.
      throw new AuthRequestError(axios.isAxiosError(error) ? error.response?.status || 0 : 0);
    }
  }
  function parseSession(body: unknown): SessionResult {
    if (!body || typeof body !== 'object' || !('data' in body)) throw new AuthRequestError(502);
    const data = body.data as Record<string, unknown> | null;
    if (!data || typeof data.access_token !== 'string' || !data.access_token ||
      /\s/.test(data.access_token) || data.access_token.length > 8192 ||
      typeof data.expires_in !== 'number' || !Number.isSafeInteger(data.expires_in) || data.expires_in <= 0) {
      throw new AuthRequestError(502);
    }
    try {
      return { accessToken: data.access_token, expiresIn: data.expires_in, user: parseIdentity(data.user) };
    } catch {
      throw new AuthRequestError(502);
    }
  }
  return {
    login: async credentials => parseSession(await request('/auth/login', credentials)),
    refresh: async () => parseSession(await request('/auth/refresh', {})),
    logout: async () => { await request('/auth/logout', {}); },
    changePassword: async (token, current, next) => {
      await request('/auth/change-password', { current_password: current, new_password: next }, token);
    },
    logoutAll: async (token, current) => { await request('/auth/logout-all', { current_password: current }, token); },
    revokeSession: async (token, id) => { await request(`/auth/sessions/${encodeURIComponent(id)}`, undefined, token, 'delete'); },
  };
}
